import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Chip,
  Avatar,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  useTheme,
  Switch,
  FormControlLabel,
  Divider,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  Print as PrintIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  AttachMoney as MoneyIcon,
  Category as CategoryIcon,
  Note as NoteIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { despesaService, tipoDespesaService, usuarioService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// =====================================================
// TIPOS E INTERFACES
// =====================================================

interface Despesa {
  id: number;
  id_usuario?: number;
  id_tipo_despesa: number;
  tipo_despesa_nome?: string;
  descricao: string;
  valor: number;
  data_cadastro: string;
  data_vencimento?: string;
  data_pagamento?: string;
  status: string;
  forma_pagamento?: string;
  observacoes?: string;
  comprovante?: any;
  ativo: boolean;
  ultima_atualizacao?: string;
}

interface DespesaFormData {
  tipo_despesa_id: string;
  data_vencimento: string;
  descricao: string;
  valor: string;
  observacoes: string;
}

interface TipoDespesa {
  id: number;
  nome: string;
  descricao?: string;
}

interface Usuario {
  id: number;
  nome: string;
  email: string;
}

interface DeleteDialogState {
  open: boolean;
  id: number | null;
}

// =====================================================
// FUNÇÕES DE FORMATAÇÃO E VALIDAÇÃO
// =====================================================

const formatarData = (data: string): string => {
  if (!data) return '';
  try {
    // Tenta diferentes formatos de data
    let dataObj;
    if (data.includes('T')) {
      // Formato ISO (2024-01-15T10:30:00)
      dataObj = new Date(data);
    } else if (data.includes('-')) {
      // Formato YYYY-MM-DD
      dataObj = new Date(data + 'T00:00:00');
    } else {
      dataObj = new Date(data);
    }
    
    if (isNaN(dataObj.getTime())) {
      return data; // Retorna o valor original se não conseguir converter
    }
    
    return dataObj.toLocaleDateString('pt-BR');
  } catch (error) {
    console.log('Erro ao formatar data:', data, error);
    return data; // Retorna o valor original em caso de erro
  }
};

const formatarDataHora = (data: string): string => {
  if (!data) return '';
  const dataObj = new Date(data);
  return dataObj.toLocaleString('pt-BR');
};

const formatarValor = (valor: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(valor);
};

const validarData = (data: string): boolean => {
  if (!data) return false;
  const dataObj = new Date(data);
  return !isNaN(dataObj.getTime());
};

const validarValor = (valor: string): boolean => {
  const valorNumerico = parseFloat(valor.replace(/[^\d,.-]/g, '').replace(',', '.'));
  return !isNaN(valorNumerico) && valorNumerico > 0;
};

const validarCamposObrigatorios = (formData: DespesaFormData): Record<string, string> => {
  const erros: Record<string, string> = {};
  
  if (!formData.tipo_despesa_id) {
    erros.tipo_despesa_id = 'Tipo de despesa é obrigatório';
  }
  
  if (!formData.data_vencimento) {
    erros.data_vencimento = 'Data de vencimento é obrigatória';
  } else if (!validarData(formData.data_vencimento)) {
    erros.data_vencimento = 'Data de vencimento inválida';
  }
  
  if (!formData.descricao?.trim()) {
    erros.descricao = 'Descrição é obrigatória';
  }
  
  if (!formData.valor?.trim()) {
    erros.valor = 'Valor é obrigatório';
  } else if (!validarValor(formData.valor)) {
    erros.valor = 'Valor inválido';
  }
  
  return erros;
};

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const Despesas: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  
  // =====================================================
  // ESTADOS PRINCIPAIS
  // =====================================================
  const [despesas, setDespesas] = useState<Despesa[]>([]);
  const [tiposDespesa, setTiposDespesa] = useState<TipoDespesa[]>([]);
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  
  // Estados do formulário
  const [formData, setFormData] = useState<DespesaFormData>({
    tipo_despesa_id: '',
    data_vencimento: '',
    descricao: '',
    valor: '',
    observacoes: ''
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  
  // Estados de controle
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [filtroDataInicial, setFiltroDataInicial] = useState<string>('');
  const [filtroDataFinal, setFiltroDataFinal] = useState<string>('');
  const [filtroTipoDespesa, setFiltroTipoDespesa] = useState<string>('');
  const [filteredDespesas, setFilteredDespesas] = useState<Despesa[]>([]);
  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({ open: false, id: null });

  // =====================================================
  // CARREGAR DADOS INICIAIS
  // =====================================================
  const carregarDados = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 [DEBUG] Carregando despesas para usuário:', user?.id);
      
      // Carregar despesas do usuário logado
      const despesasResponse = await despesaService.buscarPorUsuario(user.id);
      console.log('🔍 [DEBUG] Resposta completa da API despesas:', despesasResponse);
      
      // Tentar diferentes estruturas de resposta
      let despesasData;
      if (despesasResponse.data) {
        // Se tem .data, pode ser axios response ou estrutura aninhada
        if (Array.isArray(despesasResponse.data)) {
          despesasData = despesasResponse.data;
        } else if (despesasResponse.data.data && Array.isArray(despesasResponse.data.data)) {
          // Estrutura: { data: { data: [...] } }
          despesasData = despesasResponse.data.data;
        } else if (despesasResponse.data.despesas && Array.isArray(despesasResponse.data.despesas)) {
          // Estrutura: { data: { despesas: [...] } }
          despesasData = despesasResponse.data.despesas;
        } else {
          // Tentar usar o objeto diretamente se não for array
          despesasData = despesasResponse.data;
        }
      } else {
        // Resposta direta
        despesasData = despesasResponse;
      }
      
      console.log('🔍 [DEBUG] Dados extraídos das despesas:', despesasData);
      console.log('🔍 [DEBUG] Tipo dos dados:', typeof despesasData, 'É array:', Array.isArray(despesasData));
      
      if (Array.isArray(despesasData)) {
        setDespesas(despesasData);
        console.log('✅ [DEBUG] Despesas carregadas com sucesso:', despesasData.length, 'itens');
      } else {
        console.error('❌ [DEBUG] Dados de despesas não são um array:', despesasData);
        console.error('❌ [DEBUG] Estrutura do objeto:', Object.keys(despesasData || {}));
        setDespesas([]);
      }
      
      // Carregar tipos de despesa
      console.log('🔍 [DEBUG] Carregando tipos de despesa...');
      const tiposResponse = await tipoDespesaService.listarPorUsuario(user.id);
      console.log('🔍 [DEBUG] Resposta tipos de despesa:', tiposResponse);
      
      let tiposData;
      if (tiposResponse.data) {
        if (Array.isArray(tiposResponse.data)) {
          tiposData = tiposResponse.data;
        } else if (tiposResponse.data.data && Array.isArray(tiposResponse.data.data)) {
          tiposData = tiposResponse.data.data;
        } else if (tiposResponse.data.tipos && Array.isArray(tiposResponse.data.tipos)) {
          tiposData = tiposResponse.data.tipos;
        } else {
          tiposData = tiposResponse.data;
        }
      } else {
        tiposData = tiposResponse;
      }
      
      if (Array.isArray(tiposData)) {
        setTiposDespesa(tiposData);
        console.log('✅ [DEBUG] Tipos de despesa carregados:', tiposData.length, 'itens');
      } else {
        console.error('❌ [DEBUG] Dados de tipos não são um array:', tiposData);
        setTiposDespesa([]);
      }
      
    } catch (error) {
      console.error('💥 [DEBUG] Erro ao carregar dados:', error);
      setError('Erro ao carregar dados. Tente novamente.');
      setDespesas([]);
      setTiposDespesa([]);
    } finally {
      setLoading(false);
    }
  };

  const carregarDespesa = async (despesaId: number): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 [DEBUG] Carregando despesa para edição, ID:', despesaId);
      
      const despesaResponse = await despesaService.buscarPorId(despesaId);
      console.log('🔍 [DEBUG] Resposta completa da API buscarPorId:', despesaResponse);
      
      // Tentar diferentes estruturas de resposta
      let despesa;
      if (despesaResponse.data) {
        if (despesaResponse.data.data) {
          // Estrutura: { data: { data: {...} } }
          despesa = despesaResponse.data.data;
        } else if (despesaResponse.data.despesa) {
          // Estrutura: { data: { despesa: {...} } }
          despesa = despesaResponse.data.despesa;
        } else {
          // Estrutura: { data: {...} }
          despesa = despesaResponse.data;
        }
      } else {
        // Resposta direta
        despesa = despesaResponse;
      }
      
      console.log('🔍 [DEBUG] Dados da despesa extraídos:', despesa);
      console.log('🔍 [DEBUG] Campos disponíveis:', Object.keys(despesa || {}));
      
      if (!despesa || !despesa.id) {
        throw new Error('Despesa não encontrada ou dados inválidos');
      }
      
      // Mapear campos com logs detalhados
      console.log('🔍 [DEBUG] Mapeando campos:');
      console.log('  - id_tipo_despesa:', despesa.id_tipo_despesa);
      console.log('  - data_vencimento:', despesa.data_vencimento);
      console.log('  - data_cadastro:', despesa.data_cadastro);
      console.log('  - descricao:', despesa.descricao);
      console.log('  - valor:', despesa.valor);
      console.log('  - observacoes:', despesa.observacoes);
      
      setFormData({
        tipo_despesa_id: despesa.id_tipo_despesa?.toString() || '',
        data_vencimento: despesa.data_vencimento || despesa.data_cadastro?.split('T')[0] || '',
        descricao: despesa.descricao || '',
        valor: despesa.valor?.toString() || '',
        observacoes: despesa.observacoes || '',
      });
      
      console.log('✅ [DEBUG] FormData configurado para edição');
      setIsEditing(true);
      
    } catch (error) {
      console.error('💥 [DEBUG] Erro detalhado ao carregar despesa:', error);
      if (error instanceof Error) {
        console.error('💥 [DEBUG] Stack trace:', error.stack);
      }
      setError('Erro ao carregar despesa. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof DespesaFormData, value: string): void => {
    if (field === 'valor') {
      const valorLimpo = value.replace(/[^0-9.,]/g, '');
      setFormData(prev => ({ ...prev, [field]: valorLimpo }));
    } else {
      setFormData(prev => ({ ...prev, [field]: value }));
    }
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    
    const validationErrors = validarCamposObrigatorios(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    try {
      setSaving(true);
      setError('');
      
      const despesaData = {
        id_tipo_despesa: parseInt(formData.tipo_despesa_id),
        data_vencimento: formData.data_vencimento,
        descricao: formData.descricao.trim(),
        valor: parseFloat(formData.valor.replace(',', '.')),
        observacoes: formData.observacoes?.trim() || null,
        status: 'PENDENTE',
        ativo: true,
        id_usuario: parseInt(user?.id) || 1 // Converter para número inteiro
      };
      
      console.log('🔍 [DEBUG] Dados da despesa para salvar:', despesaData);
      
      if (id && id !== 'novo') {
        console.log('🔍 [DEBUG] Atualizando despesa existente, ID:', id);
        const response = await despesaService.atualizar(parseInt(id), despesaData);
        console.log('✅ [DEBUG] Resposta da atualização:', response);
        setSuccess('Despesa atualizada com sucesso!');
        
        // Timeout para limpar mensagem de sucesso
        setTimeout(() => {
          setSuccess(null);
        }, 5000);
        
        // Recarregar dados após edição
        carregarDados();
        
      } else {
        console.log('🔍 [DEBUG] Criando nova despesa');
        const response = await despesaService.criar(despesaData);
        console.log('✅ [DEBUG] Resposta da criação:', response);
        setSuccess('Despesa criada com sucesso!');
        
        // Recarregar dados após criação para atualizar a lista
        await carregarDados();
        
        setTimeout(() => {
          setSuccess(null);
        }, 5000);
      }
      
      setTimeout(() => {
        navigate('/app/despesas');
      }, 1500);
      
    } catch (error) {
      console.error('Erro ao salvar despesa:', error);
      setError('Erro ao salvar despesa. Verifique os dados e tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = (): void => {
    navigate('/app/despesas');
  };

  const handleNew = (): void => {
    navigate('/app/despesas/novo');
  };

  const handleEdit = (despesa: Despesa): void => {
    navigate(`/app/despesas/editar/${despesa.id}`);
  };

  const handleDelete = async (): Promise<void> => {
    if (!deleteDialog.id) return;
    
    try {
      setLoading(true);
      await despesaService.excluir(deleteDialog.id);
      setSuccess('Despesa excluída com sucesso!');
      
      setTimeout(() => {
        setSuccess(null);
      }, 5000);
      
      await carregarDados();
      
    } catch (error) {
      console.error('Erro ao excluir despesa:', error);
      setError('Erro ao excluir despesa. Tente novamente.');
    } finally {
      setLoading(false);
      setDeleteDialog({ open: false, id: null });
    }
  };

  const limparFormulario = (): void => {
    setFormData({
      tipo_despesa_id: '',
      data_vencimento: '',
      descricao: '',
      valor: '',
      observacoes: ''
    });
    setError('');
    setIsEditing(false);
  };

  const limparFiltros = (): void => {
    setFiltroDataInicial('');
    setFiltroDataFinal('');
    setFiltroTipoDespesa('');
  };

  useEffect(() => {
    carregarDados();
  }, []);

  useEffect(() => {
    if (id && id !== 'novo') {
      carregarDespesa(parseInt(id));
    } else if (id === 'novo') {
      // Limpar completamente o formulário para nova despesa
      setFormData({
        tipo_despesa_id: '',
        data_vencimento: new Date().toISOString().split('T')[0],
        descricao: '',
        valor: '',
        observacoes: ''
      });
      setErrors({});
      setIsEditing(false);
      setSuccess(null);
      setError('');
      console.log('🔍 [DEBUG] Formulário limpo para nova despesa');
    }
  }, [id]);

  // Filtrar despesas
  useEffect(() => {
    if (!Array.isArray(despesas) || despesas.length === 0) {
      setFilteredDespesas([]);
      return;
    }

    let despesasFiltradas = despesas;

    if (filtroDataInicial) {
      despesasFiltradas = despesasFiltradas.filter((despesa: Despesa) => {
        const dataDespesa = despesa.data_vencimento || despesa.data_cadastro;
        return dataDespesa >= filtroDataInicial;
      });
    }

    if (filtroDataFinal) {
      despesasFiltradas = despesasFiltradas.filter((despesa: Despesa) => {
        const dataDespesa = despesa.data_vencimento || despesa.data_cadastro;
        return dataDespesa <= filtroDataFinal;
      });
    }

    if (filtroTipoDespesa) {
      despesasFiltradas = despesasFiltradas.filter((despesa: Despesa) => {
        const tipoId = despesa.id_tipo_despesa;
        return tipoId?.toString() === filtroTipoDespesa;
      });
    }

    setFilteredDespesas(despesasFiltradas);
  }, [despesas, filtroDataInicial, filtroDataFinal, filtroTipoDespesa]);

  // =====================================================
  // RENDERIZAÇÃO DO FORMULÁRIO (EDIÇÃO/CRIAÇÃO)
  if (id) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton onClick={handleCancel} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" component="h1">
                {id !== 'novo' && isEditing ? 'Editar Despesa' : 'Nova Despesa'}
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Data de Vencimento"
                    type="date"
                    value={formData.data_vencimento}
                    onChange={(e) => handleInputChange('data_vencimento', e.target.value)}
                    required
                    disabled={loading}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <CalendarIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
            
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth required>
                    <InputLabel>Tipo de Despesa</InputLabel>
                    <Select
                      value={formData.tipo_despesa_id}
                      onChange={(e) => handleInputChange('tipo_despesa_id', e.target.value)}
                      label="Tipo de Despesa"
                      disabled={loading}
                    >
                      {tiposDespesa.map((tipo) => (
                        <MenuItem key={tipo.id} value={tipo.id}>
                          {tipo.nome}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Valor"
                    value={formData.valor}
                    onChange={(e) => handleInputChange('valor', e.target.value)}
                    required
                    disabled={loading}
                    placeholder="0,00"
                    inputProps={{
                      inputMode: 'decimal',
                      pattern: '[0-9]*[.,]?[0-9]*'
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MoneyIcon />
                        </InputAdornment>
                      ),
                    }}
                    error={!!errors.valor}
                    helperText={errors.valor}
                  />
                </Grid>
            
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Descrição"
                    value={formData.descricao}
                    onChange={(e) => handleInputChange('descricao', e.target.value)}
                    required
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <DescriptionIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
            
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Observações"
                    value={formData.observacoes}
                    onChange={(e) => handleInputChange('observacoes', e.target.value)}
                    multiline
                    rows={3}
                    disabled={loading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <NoteIcon />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
            
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={saving}
                      startIcon={<CancelIcon />}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={saving}
                      startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                      {saving ? 'Salvando...' : 'Salvar'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // =====================================================
  // RENDERIZAÇÃO DA LISTA DE DESPESAS
  // =====================================================
  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={() => navigate(-1)} color="primary">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" component="h1">
                Despesas
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleNew}
            >
              Nova Despesa
            </Button>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box sx={{ mb: 3 }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12} sm={2.4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data Inicial"
                  value={filtroDataInicial}
                  onChange={(e) => setFiltroDataInicial(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={2.4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data Final"
                  value={filtroDataFinal}
                  onChange={(e) => setFiltroDataFinal(e.target.value)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={5.4}>
                <FormControl fullWidth>
                  <InputLabel>Tipo de Despesa</InputLabel>
                  <Select
                    value={filtroTipoDespesa}
                    onChange={(e) => setFiltroTipoDespesa(e.target.value)}
                    label="Tipo de Despesa"
                  >
                    <MenuItem value="">
                      <em>Todos os tipos</em>
                    </MenuItem>
                    {tiposDespesa.map((tipo) => (
                      <MenuItem key={tipo.id} value={tipo.id.toString()}>
                        {tipo.nome}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={1.8} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  onClick={limparFiltros}
                  disabled={!filtroDataInicial && !filtroDataFinal && !filtroTipoDespesa}
                  sx={{ height: '56px', width: '100%' }}
                >
                  Limpar Filtros
                </Button>
              </Grid>
            </Grid>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Data</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Descrição</TableCell>
                    <TableCell align="right">Valor</TableCell>
                    <TableCell>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDespesas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        Nenhuma despesa encontrada
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredDespesas.map((despesa, index) => {
                      const tipoDespesa = tiposDespesa.find(t => t.id === despesa.id_tipo_despesa);
                      return (
                        <TableRow 
                          key={despesa.id} 
                          sx={{ 
                            height: '32px',
                            backgroundColor: index % 2 === 0 ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
                            '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.08)' }
                          }}
                        >  
                          <TableCell sx={{ py: 0.5 }}>
                            {formatarData(despesa.data_vencimento || despesa.data_cadastro)}
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <Chip 
                              label={despesa.tipo_despesa_nome || tipoDespesa?.nome || 'Tipo não encontrado'} 
                              color="secondary" 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <Tooltip title={despesa.descricao}>
                              <Typography noWrap sx={{ maxWidth: 200 }}>
                                {despesa.descricao}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.5, pr: 3 }}>
                            <Typography variant="body2" color="error.main" fontWeight="bold">
                              {formatarValor(despesa.valor)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 0.5 }}>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="Editar">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEdit(despesa)}
                                  color="primary"
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Excluir">
                                <IconButton
                                  size="small"
                                  onClick={() => setDeleteDialog({ open: true, id: despesa.id })}
                                  color="error"
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}>
        <DialogTitle>Confirmar Exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir esta despesa? Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, id: null })}>
            Cancelar
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Excluir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Despesas;
